/**
 * Centralized Date Service for TrackerV2
 * 
 * This service ensures consistent date handling across the entire application.
 * ALL date operations should use this service to avoid timezone and format issues.
 * 
 * STANDARD: All API communication uses UTC ISO YYYY-MM-DD format
 * DISPLAY: Local timezone formatting for user interface
 */

import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import timezoneService from '../services/timezoneService';

export class DateService {
  /**
   * Convert any date input to standardized UTC ISO date string (YYYY-MM-DD)
   * This is the ONLY format used for API communication and grouping
   */
  static toStandardDate(input: Date | string): string {
    // eslint-disable-next-line no-restricted-syntax
    const date = typeof input === 'string' ? new Date(input) : input;
    
    // Always use UTC to avoid timezone issues
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  /**
   * Alias for toStandardDate - explicitly named for UTC day key usage
   * Use this when you need to group/compare dates by UTC day
   */
  static toUTCDateKey(input: Date | string): string {
    return this.toStandardDate(input);
  }

  /**
   * Get start of day in UTC for a given date key
   * @param dateKey YYYY-MM-DD format
   * @returns ISO string for start of day (00:00:00.000Z)
   */
  static dayStartUTC(dateKey: string): string {
    return `${dateKey}T00:00:00.000Z`;
  }

  /**
   * Get end of day in UTC for a given date key
   * @param dateKey YYYY-MM-DD format
   * @returns ISO string for end of day (23:59:59.999Z)
   */
  static dayEndUTC(dateKey: string): string {
    return `${dateKey}T23:59:59.999Z`;
  }

  /**
   * Convert any date input to display format for user interface
   * FIXED: Now uses user's timezone to avoid off-by-one date errors
   * @param input Date object or string (YYYY-MM-DD or ISO format)
   * @returns Formatted date string like "Thu Oct 02 2025"
   */
  static toDisplayDate(input: Date | string): string {
    if (typeof input === 'string') {
      // If it's a YYYY-MM-DD string, parse in user timezone
      if (input.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const parsed = parseISO(input);
        const zoned = toZonedTime(parsed, timezoneService.getUserTimezone());
        return timezoneService.formatUserDate(zoned, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }
    }
    
    // For Date objects or other formats, convert to user timezone
    // eslint-disable-next-line no-restricted-syntax
    const date = typeof input === 'string' ? new Date(input) : input;
    const zoned = toZonedTime(date, timezoneService.getUserTimezone());
    return timezoneService.formatUserDate(zoned, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Parse standard date (YYYY-MM-DD) in user's timezone
   * Ensures "2025-10-02" is interpreted as midnight in user timezone, not UTC midnight
   * This prevents off-by-one date errors when displaying dates
   * 
   * @param dateKey YYYY-MM-DD format
   * @returns Date object representing midnight in user's timezone
   */
  static parseStandardDateInUserTimezone(dateKey: string): Date {
    const parsed = parseISO(dateKey); // Creates Date at midnight UTC
    return toZonedTime(parsed, timezoneService.getUserTimezone());
  }

  /**
   * Convert Date object to YYYY-MM-DD in user's timezone
   * This prevents off-by-one date errors when converting Date objects to date strings
   * 
   * @param date Date object
   * @returns YYYY-MM-DD string in user's timezone
   */
  static dateToUserDateString(date: Date): string {
    const zoned = toZonedTime(date, timezoneService.getUserTimezone());
    const year = zoned.getFullYear();
    const month = String(zoned.getMonth() + 1).padStart(2, '0');
    const day = String(zoned.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get today's date in standard format (UTC YYYY-MM-DD)
   */
  static getToday(): string {
    // eslint-disable-next-line no-restricted-syntax
    return this.toStandardDate(new Date());
  }

  /**
   * Get yesterday's date in standard format (UTC YYYY-MM-DD)
   */
  static getYesterday(): string {
    // eslint-disable-next-line no-restricted-syntax
    const yesterday = new Date();
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    return this.toStandardDate(yesterday);
  }

  /**
   * Check if two dates are the same day (using standard format)
   */
  static isSameDay(date1: Date | string, date2: Date | string): boolean {
    return this.toStandardDate(date1) === this.toStandardDate(date2);
  }

  /**
   * Group items by date using standard format as key
   */
  static groupByStandardDate<T extends { timestamp: Date | string }>(items: T[]): { [dateKey: string]: T[] } {
    const groups: { [dateKey: string]: T[] } = {};
    
    items.forEach(item => {
      const standardDate = this.toStandardDate(item.timestamp);
      
      if (!groups[standardDate]) {
        groups[standardDate] = [];
      }
      groups[standardDate].push(item);
    });
    
    return groups;
  }

  /**
   * Convert standard date to display header ("Today", "Yesterday", etc.)
   * FIXED: Now uses user's timezone for proper date comparison and formatting
   */
  static formatDateHeader(standardDate: string): string {
    // Get today and yesterday in user's timezone
    const todayUserTZ = timezoneService.getCurrentUserDate(); // YYYY-MM-DD in user's timezone
    const yesterdayUserTZ = timezoneService.getYesterdayUserDate(); // YYYY-MM-DD in user's timezone
    
    console.log('🔧 formatDateHeader comparison:', {
      standardDate,
      todayUserTZ,
      yesterdayUserTZ,
      userTimezone: timezoneService.getUserTimezone()
    });
    
    if (standardDate === todayUserTZ) {
      return 'Today';
    } else if (standardDate === yesterdayUserTZ) {
      return 'Yesterday';
    } else {
      // FIX: Parse date in user's timezone instead of UTC to avoid off-by-one errors
      const date = this.parseStandardDateInUserTimezone(standardDate);
      const todayDate = this.parseStandardDateInUserTimezone(todayUserTZ);
      
      // Calculate days difference
      const daysDiff = Math.floor(
        (todayDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysDiff <= 7) {
        // Within a week - show weekday and date
        return timezoneService.formatUserDate(date, { 
          weekday: 'long', 
          month: 'short', 
          day: 'numeric' 
        });
      } else {
        // Older than a week - show full date
        return timezoneService.formatUserDate(date, { 
          weekday: 'short',
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      }
    }
  }

  /**
   * Convert display date (toDateString format) to standard date
   * This handles the legacy conversion from "Sat Sep 27 2025" to "2025-09-27"
   */
  static displayToStandardDate(displayDate: string): string {
    if (displayDate.includes('-')) {
      // Already in standard format
      return displayDate;
    }
    
    // From toDateString() format
    // eslint-disable-next-line no-restricted-syntax
    const date = new Date(displayDate);
    return this.toStandardDate(date);
  }

  /**
   * Get date range for time range parameter
   */
  static getDateRangeForTimeRange(timeRange: string): { startDate: string; endDate: string } {
    // eslint-disable-next-line no-restricted-syntax
    const today = new Date();
    const endDate = this.toStandardDate(today);
    
    let startDate: string;
    
    switch (timeRange) {
      case '1d':
        startDate = endDate; // Same day
        break;
      case '7d':
      case '1w':
        // eslint-disable-next-line no-restricted-syntax
        const weekStart = new Date(today);
        weekStart.setUTCDate(today.getUTCDate() - 6); // Last 7 days including today
        startDate = this.toStandardDate(weekStart);
        break;
      case '30d':
      case '1m':
        // eslint-disable-next-line no-restricted-syntax
        const monthStart = new Date(today);
        monthStart.setUTCDate(today.getUTCDate() - 29); // Last 30 days including today
        startDate = this.toStandardDate(monthStart);
        break;
      default:
        // Default to 7 days
        // eslint-disable-next-line no-restricted-syntax
        const defaultStart = new Date(today);
        defaultStart.setUTCDate(today.getUTCDate() - 6);
        startDate = this.toStandardDate(defaultStart);
    }
    
    return { startDate, endDate };
  }

  /**
   * Convert user's local date to UTC range
   * User selects "2025-09-30" in their timezone → returns UTC start/end that covers their full local day
   * 
   * Example: User in UTC-7 selects "2025-09-30"
   * - Local day: 2025-09-30 00:00 local = 2025-09-30 07:00 UTC
   * - Local day end: 2025-09-30 23:59 local = 2025-10-01 06:59 UTC
   * 
   * @param dateKey YYYY-MM-DD in user's local timezone
   * @returns { startUTC, endUTC } ISO strings covering full local day
   */
  static userDateToUTCRange(dateKey: string): { startUTC: string; endUTC: string } {
    // Use timezoneService for proper timezone conversion
    // Create Date objects representing the local times
    // eslint-disable-next-line no-restricted-syntax
    const localMidnight = new Date(dateKey + 'T00:00:00');
    // eslint-disable-next-line no-restricted-syntax
    const localEndOfDay = new Date(dateKey + 'T23:59:59.999');
    
    // Convert to UTC using timezoneService (uses date-fns-tz under the hood)
    const startUTC = timezoneService.convertUserTimeToUTC(localMidnight);
    const endUTC = timezoneService.convertUserTimeToUTC(localEndOfDay);
    
    console.log('🔧 userDateToUTCRange conversion:', {
      dateKey,
      localMidnight: localMidnight.toString(),
      localEndOfDay: localEndOfDay.toString(),
      startUTC,
      endUTC,
      userTimezone: timezoneService.getUserTimezone()
    });
    
    return { startUTC, endUTC };
  }

  /**
   * Get start of week (Monday) for a given date key
   * @param dateKey YYYY-MM-DD format
   * @returns YYYY-MM-DD for Monday of that week
   */
  static getStartOfWeek(dateKey: string): string {
    // eslint-disable-next-line no-restricted-syntax
    const date = new Date(dateKey + 'T12:00:00.000Z');
    const dayOfWeek = date.getUTCDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    // eslint-disable-next-line no-restricted-syntax
    const monday = new Date(date);
    monday.setUTCDate(date.getUTCDate() - daysToMonday);
    return this.toStandardDate(monday);
  }

  /**
   * Get end of week (Sunday) for a given date key
   * @param dateKey YYYY-MM-DD format
   * @returns YYYY-MM-DD for Sunday of that week
   */
  static getEndOfWeek(dateKey: string): string {
    const monday = this.getStartOfWeek(dateKey);
    // eslint-disable-next-line no-restricted-syntax
    const sunday = new Date(monday + 'T12:00:00.000Z');
    sunday.setUTCDate(sunday.getUTCDate() + 6);
    return this.toStandardDate(sunday);
  }

  /**
   * Get start of month (1st day) for a given date key
   * @param dateKey YYYY-MM-DD format
   * @returns YYYY-MM-DD for first day of that month
   */
  static getStartOfMonth(dateKey: string): string {
    const [year, month] = dateKey.split('-');
    return `${year}-${month}-01`;
  }

  /**
   * Get end of month (last day) for a given date key
   * @param dateKey YYYY-MM-DD format
   * @returns YYYY-MM-DD for last day of that month
   */
  static getEndOfMonth(dateKey: string): string {
    const [year, month] = dateKey.split('-').map(Number);
    // Calculate days in month
    // eslint-disable-next-line no-restricted-syntax
    const daysInMonth = new Date(year, month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
  }

  /**
   * Debug function to log date conversion
   */
  static debugDateConversion(input: string | Date, context: string): void {
    const standardDate = this.toStandardDate(input);
    const displayDate = this.toDisplayDate(input);
    
    console.log(`🔧 DATE CONVERSION [${context}]:`, {
      input: typeof input === 'string' ? input : input.toISOString(),
      standardDate,
      displayDate,
      // eslint-disable-next-line no-restricted-syntax
      timestamp: new Date().toISOString()
    });
  }
}

// Export individual functions for convenience
export const {
  toStandardDate,
  toUTCDateKey,
  dayStartUTC,
  dayEndUTC,
  toDisplayDate,
  parseStandardDateInUserTimezone,
  dateToUserDateString,
  getToday,
  getYesterday,
  isSameDay,
  groupByStandardDate,
  formatDateHeader,
  displayToStandardDate,
  getDateRangeForTimeRange,
  userDateToUTCRange,
  debugDateConversion
} = DateService;
